/* ==========================================================================
   From The Bronx · ftb-policy-index.js · added 2026-09-01
   Builds the "on this page" index on the Shopify policy pages.

   [!] THE POLICY PAGES HAVE NO TEMPLATE AND NO SECTION. Shopify renders them
   from its own .shopify-policy__* markup, so there is nowhere to put this in
   Liquid. The index is built here at runtime from the <h2>s the policy body
   already contains, and appended to .shopify-policy__container.

   Runs on /policies/refund-policy, /policies/shipping-policy,
   /policies/privacy-policy and /policies/terms-of-service alike, because it
   keys off the markup rather than the path. It no-ops everywhere else, and
   no-ops on any policy page with fewer than three headings, which is why the
   two-line contact information policy correctly gets nothing.

   ONE LIST, TWO FORMS. A <details> disclosure on small screens, a sticky rail
   from 1100px up. The width switch is the media query below; the layout for
   both forms is in ftb-policy-index.css. No inline styles are set here.
   ========================================================================== */
(function () {
  'use strict';

  var MIN_HEADINGS = 3;
  var DESKTOP = '(min-width: 1100px)';

  function slugify(text, index) {
    var slug = String(text)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return 'ftb-policy-' + (slug || 'section') + '-' + index;
  }

  function build() {
    var container = document.querySelector('.shopify-policy__container');
    if (!container) return;
    if (container.querySelector('.ftb-policy-index')) return;

    var body = container.querySelector('.shopify-policy__body .rte');
    if (!body) return;

    var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));
    if (headings.length < MIN_HEADINGS) return;

    var nav = document.createElement('nav');
    nav.className = 'ftb-policy-index';
    nav.setAttribute('aria-label', 'On this page');

    var wrap = document.createElement('details');
    wrap.className = 'ftb-policy-index__wrap';

    var summary = document.createElement('summary');
    summary.className = 'ftb-policy-index__summary';
    summary.textContent = 'Jump to a section';
    wrap.appendChild(summary);

    var inner = document.createElement('div');
    inner.className = 'ftb-policy-index__inner';

    var title = document.createElement('p');
    title.className = 'ftb-policy-index__title';
    title.textContent = 'On this page';
    inner.appendChild(title);

    var list = document.createElement('ul');
    list.className = 'ftb-policy-index__list';

    var links = [];

    headings.forEach(function (heading, i) {
      var text = (heading.textContent || '').trim();
      if (!text) return;

      if (!heading.id) heading.id = slugify(text, i + 1);

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'ftb-policy-index__link';
      a.href = '#' + heading.id;
      a.textContent = text;

      li.appendChild(a);
      list.appendChild(li);
      links.push({ link: a, heading: heading });
    });

    if (!links.length) return;

    inner.appendChild(list);
    wrap.appendChild(inner);
    nav.appendChild(wrap);
    container.appendChild(nav);

    openOnDesktop(wrap);
    closeOnJump(wrap, links);

    var current = null;
    function setCurrent(entry) {
      if (entry === current) return;
      links.forEach(function (pair) {
        if (pair === entry) {
          pair.link.setAttribute('aria-current', 'true');
        } else {
          pair.link.removeAttribute('aria-current');
        }
      });
      current = entry;
    }

    spy(links, setCurrent);
  }

  /* The rail has no toggle, so <details> is forced open above the breakpoint.
     Kept in sync on resize and on orientation change. */
  function openOnDesktop(wrap) {
    if (!window.matchMedia) {
      wrap.open = true;
      return;
    }
    var mq = window.matchMedia(DESKTOP);

    function apply() {
      wrap.open = mq.matches;
    }
    apply();

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(apply);
    }
  }

  /* On small screens, picking a section closes the list behind you. */
  function closeOnJump(wrap, links) {
    links.forEach(function (pair) {
      pair.link.addEventListener('click', function () {
        if (window.matchMedia && window.matchMedia(DESKTOP).matches) return;
        wrap.open = false;
      });
    });
  }

  /* Mark the section currently being read.

     [!] THIS WAS AN INTERSECTIONOBSERVER READING BAND AND IT WAS WRONG.
     A band that stops short of the viewport bottom can never contain the last
     few headings on a page, because the page runs out of scroll before they
     reach it. The final sections stayed permanently unmarked, and clicking
     them did nothing visible since a click only scrolls and there was nowhere
     left to scroll to. Reported 2026-09-01.

     Position-based instead: the current section is the last heading that has
     passed a line 140px down the viewport, with two explicit cases that the
     band could not express. */
  function spy(links, setCurrent) {
    var LINE = 140;
    var ticking = false;

    function atBottom() {
      var doc = document.documentElement;
      return (window.innerHeight + window.pageYOffset) >= (doc.scrollHeight - 2);
    }

    function pick() {
      /* Case 1: scrolled to the end. The last section is the one you are on,
         whether or not it ever crossed the line. */
      if (atBottom()) return links[links.length - 1];

      var chosen = links[0];
      for (var i = 0; i < links.length; i++) {
        if (links[i].heading.getBoundingClientRect().top <= LINE) {
          chosen = links[i];
        } else {
          break;
        }
      }
      return chosen;
    }

    function update() {
      ticking = false;
      setCurrent(pick());
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    /* Case 2: a click marks its own link straight away. On the last screenful
       the page cannot move, so no scroll event will ever arrive to do it. */
    links.forEach(function (pair) {
      pair.link.addEventListener('click', function () {
        setCurrent(pair);
      });
    });

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
